// Billing API endpoints

import { api } from './client'

// =============================================================================
// Types
// =============================================================================

export interface Plan {
  id: string
  name: string
  price: number
  interval: 'month' | 'year'
  clicks: number
  features: string[]
  popular?: boolean
  /** Yearly price in dollars (with annual discount applied) */
  yearlyPrice: number
  /** Yearly discount percentage (e.g. 20) */
  yearlyDiscountPercent: number
  /** Whether overage billing is enabled for this plan */
  overageEnabled?: boolean
  /** Cost per 1,000 overage clicks in USD cents */
  overageCostPer1kCents?: number
}

export interface Subscription {
  id: string
  planId: string
  planName: string
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'unpaid'
  billingPeriod: 'month' | 'quarter' | 'year'
  priceCents: number
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
}

export interface Usage {
  clicks: { used: number; limit: number }
  campaigns: { used: number; limit: number }
  domains: { used: number; limit: number }
  teamMembers: { used: number; limit: number }
  apiCalls: { used: number; limit: number }
  overageClicks?: number
  overageCostCents?: number
  isInOverage?: boolean
  isSuspended?: boolean
}

export interface PaymentMethod {
  id: string
  type: 'card' | 'paypal'
  brand?: string
  last4?: string
  expMonth?: number
  expYear?: number
  email?: string
  isDefault: boolean
}

export interface Invoice {
  id: string
  number: string
  date: string
  dueDate: string
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'failed' | 'void'
  plan: string
  pdfUrl?: string
  type?: 'subscription' | 'topup' | 'service' | 'refund'
  paymentMethod?: string
}

export interface BillingOverview {
  subscription: Subscription | null
  usage: Usage
  paymentMethods: PaymentMethod[]
  upcomingInvoice: {
    amount: number
    date: string
  } | null
}

// =============================================================================
// Balance & Top-Up Types
// =============================================================================

export interface Balance {
  balanceUsd: string
  balanceCents: number
  frozenCents: number
  currency: string
}

export interface BalanceTransaction {
  id: string
  userId: string
  txType: 'TopUp' | 'SubscriptionDebit' | 'ServiceDebit' | 'Refund' | 'Adjustment' | 'Freeze' | 'Unfreeze' | (string & {})
  amountCents: number
  balanceBeforeCents: number
  balanceAfterCents: number
  description: string
  referenceId?: string
  metadata?: Record<string, unknown>
  createdAt: string
}

export interface BalanceTransactionsResponse {
  transactions: BalanceTransaction[]
  total: number
  hasMore: boolean
}

export interface CryptoCurrency {
  code: string
  name: string
  logoUrl?: string
  network?: string
  isStable?: boolean
}

export interface CryptoEstimate {
  estimatedAmount: string
  currency: string
  rateValidUntil?: string
}

export interface MinAmountResponse {
  minAmount: string
  currency: string
  fiatEquivalent?: string
}

export type TopUpSessionStatus = 'pending' | 'confirming' | 'completed' | 'failed' | 'expired' | 'partially_paid'

export interface TopUpSession {
  sessionId: string
  payAddress: string
  payAmount: string
  payCurrency: string
  amountUsd: string
  status: TopUpSessionStatus
  expiresAt?: string
  qrData: string
  actuallyPaid?: string
  createdAt: string
}

export interface CreateTopUpRequest {
  amountUsd: number
  payCurrency: string
}

export interface ServicePayment {
  id: string
  serviceType: string
  amountCents: number
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'pending_topup'
  referenceId: string
  description: string
  balanceAfterCents?: number
  topupSession?: TopUpSession
  createdAt: string
}

export interface CreateServicePaymentRequest {
  serviceType: string
  amountCents: number
  referenceId: string
  description: string
  payCurrency?: string
}

export interface RenewalInfo {
  nextDate: string
  amountCents: number
  balanceSufficient: boolean
}

export interface OverageStatus {
  plan: string
  includedClicks: number
  clicksUsed: number
  overageClicks: number
  overageCostCents: number
  overageRatePer1kCents: number
  isInOverage: boolean
  isSuspended: boolean
  gracePeriodStart: string | null
  gracePeriodExpiresAt: string | null
  periodStart: string
  periodEnd: string
}

export interface SubscriptionFromBalanceResponse {
  subscriptionId: string
  invoiceId: string
  status: string
  balanceAfterCents: number
}

// =============================================================================
// API Client
// =============================================================================

// Base path for billing endpoints
const BASE_PATH = '/v1/billing'

export const billingApi = {
  // Get billing overview
  getOverview: async (): Promise<BillingOverview> => {
    return api.get<BillingOverview>(`${BASE_PATH}/overview`)
  },

  // Get available plans
  getPlans: async (): Promise<Plan[]> => {
    return api.get<Plan[]>(`${BASE_PATH}/plans`)
  },

  // Get current subscription
  getSubscription: async (): Promise<Subscription | null> => {
    return api.get<Subscription | null>(`${BASE_PATH}/subscription`)
  },

  // Create subscription (checkout)
  createSubscription: async (planId: string): Promise<{ checkoutUrl: string }> => {
    return api.post<{ checkoutUrl: string }>(`${BASE_PATH}/subscription`, { planId })
  },

  // Update subscription (change plan)
  updateSubscription: async (planId: string): Promise<Subscription> => {
    return api.patch<Subscription>(`${BASE_PATH}/subscription`, { planId })
  },

  // Cancel subscription
  cancelSubscription: async (immediately?: boolean): Promise<Subscription> => {
    return api.post<Subscription>(`${BASE_PATH}/subscription/cancel`, { immediately })
  },

  // Resume subscription
  resumeSubscription: async (): Promise<Subscription> => {
    return api.post<Subscription>(`${BASE_PATH}/subscription/resume`)
  },

  // Create subscription from balance (balance-based payment)
  createSubscriptionFromBalance: async (planId: string, billingInterval?: 'month' | 'year'): Promise<SubscriptionFromBalanceResponse> => {
    return api.post<SubscriptionFromBalanceResponse>(`${BASE_PATH}/subscription/from-balance`, {
      planId,
      billingInterval: billingInterval ?? 'month',
    })
  },

  // Get usage
  getUsage: async (): Promise<Usage> => {
    return api.get<Usage>(`${BASE_PATH}/usage`)
  },

  // Get payment methods
  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    return api.get<PaymentMethod[]>(`${BASE_PATH}/payment-methods`)
  },

  // Add payment method
  addPaymentMethod: async (): Promise<{ setupUrl: string }> => {
    return api.post<{ setupUrl: string }>(`${BASE_PATH}/payment-methods`)
  },

  // Remove payment method
  removePaymentMethod: async (id: string): Promise<void> => {
    return api.delete(`${BASE_PATH}/payment-methods/${id}`)
  },

  // Set default payment method
  setDefaultPaymentMethod: async (id: string): Promise<PaymentMethod> => {
    return api.post<PaymentMethod>(`${BASE_PATH}/payment-methods/${id}/default`)
  },

  // Get invoices
  getInvoices: async (params?: { page?: number; limit?: number }): Promise<{ invoices: Invoice[]; total: number }> => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return api.get<{ invoices: Invoice[]; total: number }>(`${BASE_PATH}/invoices${query}`)
  },

  // Get single invoice
  getInvoice: async (id: string): Promise<Invoice> => {
    return api.get<Invoice>(`${BASE_PATH}/invoices/${id}`)
  },

  // Download invoice PDF (uses api.getBlob for proper GET auth/retry handling)
  downloadInvoice: async (id: string): Promise<Blob> => {
    return api.getBlob(`${BASE_PATH}/invoices/${id}/pdf`)
  },

  // Create customer portal session
  createPortalSession: async (): Promise<{ url: string }> => {
    return api.post<{ url: string }>(`${BASE_PATH}/portal`)
  },

  // =========================================================================
  // Balance & Top-Up
  // =========================================================================

  // Get user balance
  getBalance: async (): Promise<Balance> => {
    return api.get<Balance>(`${BASE_PATH}/balance`)
  },

  // Get balance transaction history
  getBalanceTransactions: async (params?: { limit?: number; offset?: number }): Promise<BalanceTransactionsResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.offset) queryParams.set('offset', params.offset.toString())
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return api.get<BalanceTransactionsResponse>(`${BASE_PATH}/balance/transactions${query}`)
  },

  // Get available crypto currencies
  getCurrencies: async (): Promise<{ currencies: CryptoCurrency[] }> => {
    return api.get<{ currencies: CryptoCurrency[] }>(`${BASE_PATH}/currencies`)
  },

  // Get crypto estimate for USD amount
  getEstimate: async (amountUsd: number, currency: string): Promise<CryptoEstimate> => {
    return api.get<CryptoEstimate>(`${BASE_PATH}/estimate?amount_usd=${amountUsd}&currency=${currency}`)
  },

  // Get minimum amount for a currency
  getMinAmount: async (currency: string): Promise<MinAmountResponse> => {
    return api.get<MinAmountResponse>(`${BASE_PATH}/min-amount?currency=${currency}`)
  },

  // Create top-up session
  createTopUp: async (request: CreateTopUpRequest): Promise<TopUpSession> => {
    return api.post<TopUpSession>(`${BASE_PATH}/topup`, {
      amount_usd: request.amountUsd,
      pay_currency: request.payCurrency,
    })
  },

  // Get top-up session by ID
  getTopUpSession: async (sessionId: string): Promise<TopUpSession> => {
    return api.get<TopUpSession>(`${BASE_PATH}/topup/${sessionId}`)
  },

  // Get active (pending) top-up sessions
  getActiveTopUps: async (): Promise<TopUpSession[]> => {
    return api.get<TopUpSession[]>(`${BASE_PATH}/topup/active`)
  },

  // =========================================================================
  // Service Payments
  // =========================================================================

  // Create service payment
  createServicePayment: async (request: CreateServicePaymentRequest): Promise<ServicePayment> => {
    return api.post<ServicePayment>(`${BASE_PATH}/service-payment`, {
      service_type: request.serviceType,
      amount_cents: request.amountCents,
      reference_id: request.referenceId,
      description: request.description,
      pay_currency: request.payCurrency,
    })
  },

  // Get service payments
  getServicePayments: async (): Promise<ServicePayment[]> => {
    return api.get<ServicePayment[]>(`${BASE_PATH}/service-payments`)
  },

  // Get single service payment
  getServicePayment: async (id: string): Promise<ServicePayment> => {
    return api.get<ServicePayment>(`${BASE_PATH}/service-payments/${id}`)
  },

  // =========================================================================
  // Subscription Renewal Info
  // =========================================================================

  // Get subscription renewal info
  getRenewalInfo: async (): Promise<RenewalInfo> => {
    return api.get<RenewalInfo>(`${BASE_PATH}/subscription/renewal-info`)
  },

  // Get overage billing status
  getOverageStatus: async (): Promise<OverageStatus> => {
    return api.get<OverageStatus>(`${BASE_PATH}/overage`)
  },
}
